import React from 'react';
import styled, { css } from 'styled-components';
import { DOTS, usePagination } from '../hooks/usePagination';
import { Button } from './Button';

// Interfaces

interface PaginationPropsBase {
  currentPage: number;
  totalCount: number;
  siblingCount?: number;
  pageSize: number;
}

interface PaginationPropsWithFunction extends PaginationPropsBase {
  onPageChange: (pageNumber: number) => void;
  linkGenerator?: never;
}

interface PaginationPropsWithLink extends PaginationPropsBase {
  linkGenerator: (pageNumber: number) => string;
  onPageChange?: never;
}

type PaginationProps = PaginationPropsWithFunction | PaginationPropsWithLink;

// Styles

const StyledPagination = styled.nav`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.md};
    justify-content: center;

    // Tablet > styles

    @media (min-width: ${media.md}) {
    }
  `};
`;

const StyledPaginationPages = styled.ol`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.sm};
    justify-content: center;

    // Tablet > styles

    @media (min-width: ${media.md}) {
    }
  `};
`;

// JSX

export const Pagination = ({
  onPageChange,
  linkGenerator,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
}: PaginationProps) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  // Only show pagination if there are more than one page

  if (currentPage === 0 || paginationRange?.length < 2) {
    return null;
  }

  const nextPage = currentPage + 1;
  const prevPage = currentPage - 1;
  const lastPage = paginationRange[paginationRange.length - 1];

  // Next Page

  const handleNext = () => {
    if (onPageChange) {
      onPageChange(nextPage);
    }
  };

  // Previous page

  const handlePrevious = () => {
    if (onPageChange) {
      onPageChange(prevPage);
    }
  };

  return (
    <StyledPagination>
      {onPageChange && (
        <Button
          variant="pill"
          arrowLeft
          type="button"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'propertySearchPagination',
            clickText: 'previous page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          Previous
        </Button>
      )}
      {linkGenerator && (
        <Button
          variant="pill"
          arrowLeft
          url={linkGenerator(prevPage)}
          disabled={currentPage === 1}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'pagination',
            clickText: 'previous page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          Previous
        </Button>
      )}
      <StyledPaginationPages>
        {paginationRange?.length &&
          paginationRange.map((pageNumber, index) => {
            if (typeof pageNumber === 'string' && pageNumber === DOTS) {
              return (
                <li key={`pagination-dot-${index}`}>
                  <Button variant="pill" as="div" disabled>
                    &#8230;
                  </Button>
                </li>
              );
            }

            return (
              <li key={`pagination-${pageNumber}-${index}`}>
                {onPageChange && (
                  <Button
                    variant="pill"
                    type="button"
                    onClick={() => onPageChange(pageNumber as number)}
                    selected={pageNumber === currentPage}
                    customDataLayerEvent={{
                      event: 'pagination',
                      clickText: pageNumber,
                      paginationCurrentPage: currentPage,

                      clickHostname: undefined,
                      clickPath: undefined,
                      clickQueryString: undefined,
                      clickBladeID: undefined,
                    }}
                  >
                    {pageNumber}
                  </Button>
                )}
                {linkGenerator && (
                  <Button
                    variant="pill"
                    url={linkGenerator(pageNumber as number)}
                    selected={pageNumber === currentPage}
                    customDataLayerEvent={{
                      event: 'pagination',
                      clickText: pageNumber,
                      paginationCurrentPage: currentPage,

                      clickHostname: undefined,
                      clickPath: undefined,
                      clickQueryString: undefined,
                      clickBladeID: undefined,
                    }}
                  >
                    {pageNumber}
                  </Button>
                )}
              </li>
            );
          })}
      </StyledPaginationPages>
      {onPageChange && (
        <Button
          variant="pill"
          arrowRight
          type="button"
          onClick={handleNext}
          disabled={lastPage === currentPage}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'pagination',
            clickText: 'next page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          Next
        </Button>
      )}

      {linkGenerator && (
        <Button
          variant="pill"
          arrowRight
          url={linkGenerator(nextPage)}
          disabled={lastPage === currentPage}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'pagination',
            clickText: 'next page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          Next
        </Button>
      )}
    </StyledPagination>
  );
};
